import { formatVertex } from '@baduk/engine';
import { CONCEPTS, HINT_LEVELS, type Advice, type Observation } from '@baduk/coach';

export interface CoachPanelProps {
  readonly size: number;
  readonly observations: readonly Observation[];
  readonly advice: Advice | null;
  readonly hintIndex: number;
  readonly onHint: () => void;
  readonly onClearHint: () => void;
  readonly disabled: boolean;
  /**
   * Off while watching two bots play. There is no one to give a hint to, and a
   * "here is the answer" button would be inviting the viewer to play a move they
   * cannot make.
   */
  readonly showHints?: boolean;
  /** Whose turn the observations are about, when it is not the reader's own. */
  readonly subject?: string;
}

/**
 * The coach's voice.
 *
 * Two distinct things live here and are kept visually distinct: observations,
 * which are facts about the position and are always on, and advice, which is an
 * opinion the learner has to ask for. Blurring them would train a learner to
 * wait for instructions instead of reading the board.
 */
export function CoachPanel({
  size,
  observations,
  advice,
  hintIndex,
  onHint,
  onClearHint,
  disabled,
  showHints = true,
  subject,
}: CoachPanelProps) {
  const concept = advice?.concept ? CONCEPTS[advice.concept] : null;
  const canGoDeeper = hintIndex < HINT_LEVELS.length - 1;

  return (
    <aside className="coach">
      <div className="coach-head">
        <span className="coach-avatar" aria-hidden="true">
          🧭
        </span>
        <div>
          <h2>Coach</h2>
          <p className="muted small">
            {subject ? `On ${subject}’s position` : 'Watching the board with you'}
          </p>
        </div>
      </div>

      {observations.length === 0 && !showHints && (
        <p className="muted small">Nothing worth pointing out in this position.</p>
      )}

      {/*
        Two notes, not three, and each one a headline and a single line. The
        reasoning sits behind "why?" — a teacher says what is true every time it
        is true, and explains what liberties are once.
      */}
      {observations.length > 0 && (
        <ul className="observations">
          {observations.slice(0, 2).map((note, index) => (
            <li key={`${note.headline}-${index}`} className={`observation ${note.kind}`}>
              <strong>{note.headline}</strong>
              <p>{note.detail}</p>
              {note.why && (
                <details className="why">
                  <summary>why?</summary>
                  <p>{note.why}</p>
                  {note.concept && CONCEPTS[note.concept] && (
                    <p className="try-this">
                      <strong>Try this:</strong> {CONCEPTS[note.concept].tryThis}
                    </p>
                  )}
                </details>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="hint-box" hidden={!showHints}>
        {advice === null ? (
          <>
            <p className="muted">
              Stuck? Ask for a hint. The coach starts vague on purpose — the thinking you do before
              the answer is the part that makes you stronger.
            </p>
            <button type="button" className="primary" onClick={onHint} disabled={disabled}>
              Give me a hint
            </button>
          </>
        ) : (
          <>
            <div className="hint-level">
              Hint {hintIndex + 1} of {HINT_LEVELS.length}
            </div>
            <h3>{advice.headline}</h3>
            <p>{advice.detail}</p>
            {advice.vertex !== undefined && (
              <p className="hint-answer">
                The coach would play <strong>{formatVertex(size, advice.vertex)}</strong>.
              </p>
            )}

            {concept && (
              <details className="concept">
                <summary>{concept.title}</summary>
                <p>{concept.whyItMatters}</p>
                <p className="try-this">
                  <strong>Try this:</strong> {concept.tryThis}
                </p>
              </details>
            )}

            <div className="hint-actions">
              {canGoDeeper && (
                <button type="button" className="primary" onClick={onHint} disabled={disabled}>
                  Tell me more
                </button>
              )}
              <button type="button" onClick={onClearHint}>
                I&apos;ve got it
              </button>
            </div>

            {/*
              The shortlist, and the reason it is not tucked behind a disclosure:
              advice given only as prose makes a learner translate coordinates
              back onto the board before they can even consider it. These are
              numbered to match the markers the board is showing.
            */}
            {advice.vertex !== undefined && advice.alternatives.length > 0 && (
              <div className="shortlist">
                <h4>Where to play, best first</h4>
                <p className="muted small">
                  Numbered on the board as well. The gap between first and second is often small —
                  read the reasons rather than trusting the order.
                </p>
                <ol className="shortlist-moves">
                  {advice.alternatives.slice(0, 5).map((candidate, index) => (
                    <li key={candidate.vertex} className={index === 0 ? 'best' : undefined}>
                      <span className="rank" aria-hidden="true">
                        {index + 1}
                      </span>
                      <span>
                        <strong>{formatVertex(size, candidate.vertex)}</strong>
                        <span className="muted small">{candidate.reason}</span>
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </>
        )}
      </div>

      <p className="disclaimer small muted">
        The coach plays at about a decent club beginner&apos;s level. It is a guide, not an oracle —
        if a move looks good to you and the coach disagrees, it is worth working out why.
      </p>
    </aside>
  );
}
